import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Copy } from 'lucide-react';

import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * GroupPassCodesTab — Dashboard tab listing pass codes assigned to the user's
 * group, with copy-to-clipboard and one-click claim/activation.
 *
 * Extracted from App.js (Fase 2) — pure relocation, no logic changes.
 */
export const GroupPassCodesTab = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [claiming, setClaiming] = useState(null);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/group-pass-codes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
      });
      setCodes(res.data?.codes || []);
      setGroupName(res.data?.group || user?.group || '');
    } catch (e) {
      console.error('Failed to load group codes:', e);
      toast.error('Could not load your group codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claimCode = async (code) => {
    if (!window.confirm(`Activate pass code ${code} on your account?\n\nThis will replace any current pass.`)) return;
    setClaiming(code);
    try {
      const res = await axios.post(`${API}/my/group-pass-codes/claim`, { code }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
      });
      if (res.data?.success) {
        toast.success(`Pass code ${code} activated! Your ${res.data.pass_type} pass is now active.`);
        // Update local user state
        if (setUser) {
          setUser(prev => prev ? {
            ...prev,
            pass_code: res.data.pass_code,
            pass_type: res.data.pass_type,
            pass_expires_at: res.data.pass_expires_at,
          } : prev);
        }
        fetchCodes();
      } else {
        toast.error('Activation failed');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not activate the pass code';
      toast.error(msg);
    } finally {
      setClaiming(null);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`${code} copied to clipboard`);
  };

  const formatPrice = (p) => {
    if (p === null || p === undefined) return '—';
    const n = Number(p);
    if (Number.isNaN(n)) return String(p);
    return `€${n.toFixed(2).replace('.', ',')}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  // Compute whether the current user already has an active paid pass. If yes,
  // we must NOT allow claiming another one (it would silently overwrite).
  const hasActivePaidPass = (() => {
    const t = (user?.pass_type || '').toLowerCase();
    if (!t || t === 'free') return false;
    const exp = user?.pass_expires_at;
    if (!exp) return true; // non-free, no expiry → treat as active
    const expDt = new Date(exp);
    return !Number.isNaN(expDt.getTime()) && expDt > new Date();
  })();

  return (
    <Card className="p-6 rounded-2xl" data-testid="group-codes-tab">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Ticket className="w-5 h-5 text-primary" />
            Pass codes assigned to your group
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Group: <span className="font-medium text-foreground">{groupName || '—'}</span>
            {' · '}{codes.length} {codes.length === 1 ? 'code' : 'codes'} available
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchCodes} disabled={loading} data-testid="refresh-group-codes">
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {hasActivePaidPass && (
        <div className="mb-4 p-3 rounded-lg border border-amber-300/60 bg-amber-50/70 dark:bg-amber-900/20 text-sm" data-testid="active-pass-warning">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            You already have an active {user?.pass_type} pass ({user?.pass_code || '—'})
          </p>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/70 mt-1">
            Activating a new code would replace your current pass. To prevent loss of value, claiming is disabled —
            contact your admin to switch passes, or wait for your current pass to expire.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-24 skeleton rounded-xl" />
          ))}
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-10 border border-dashed rounded-xl text-muted-foreground" data-testid="no-group-codes">
          <Ticket className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No active pass codes for your group right now</p>
          <p className="text-sm mt-1">Your admin can assign codes to the <b>{groupName || 'your'}</b> group from the admin dashboard.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="group-codes-list">
          {codes.map((c) => {
            const isMyActive = user?.pass_code === c.code;
            return (
              <div key={c.code} className="p-4 rounded-xl border bg-secondary/20 flex flex-col md:flex-row md:items-center gap-3 md:gap-5" data-testid={`group-code-${c.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => copyCode(c.code)}
                      className="font-mono text-base font-bold bg-card px-3 py-1.5 rounded border hover:bg-primary/5 transition inline-flex items-center gap-2"
                      title="Click to copy"
                      data-testid={`copy-code-${c.code}`}
                    >
                      {c.code}
                      <Copy className="w-3.5 h-3.5 opacity-60" />
                    </button>
                    <Badge variant={c.pass_type === 'annual' ? 'default' : c.pass_type === 'b2b' ? 'outline' : 'secondary'} className="capitalize">
                      {(c.pass_type || '').replace('_', ' ')}
                    </Badge>
                    {isMyActive && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-100">
                        Active on your account
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3 text-muted-foreground">
                    <div>
                      <p className="text-xs">Price</p>
                      <p className="font-medium text-foreground">{formatPrice(c.price)}</p>
                    </div>
                    <div>
                      <p className="text-xs">Expires</p>
                      <p className="font-medium text-foreground">{formatDate(c.expires_at)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs">Note</p>
                      <p className="font-medium text-foreground truncate" title={c.notes || c.b2b_customer_name || ''}>
                        {c.notes || c.b2b_customer_name || '—'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="md:w-44 flex md:justify-end">
                  <Button
                    onClick={() => claimCode(c.code)}
                    disabled={claiming === c.code || isMyActive || (hasActivePaidPass && !isMyActive)}
                    className="w-full md:w-auto rounded-full"
                    data-testid={`claim-code-${c.code}`}
                    title={hasActivePaidPass && !isMyActive ? 'You already have an active pass — contact admin to switch' : undefined}
                  >
                    {isMyActive
                      ? 'Already active'
                      : claiming === c.code
                        ? 'Activating…'
                        : hasActivePaidPass
                          ? 'Pass already active'
                          : 'Activate on my account'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default GroupPassCodesTab;
